import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import schema from './funnelSchema.json';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'gg_guru_application_draft_v1';
const LABELS = ['YOUR OPPORTUNITY', 'ABOUT YOU', 'AVAILABILITY', 'EXPERIENCE', 'HOW YOU WORK', 'REVIEW'];
const STAGE_NAMES = ['Opportunity', 'About you', 'Availability', 'Experience', 'How you work', 'Review'];
const LEVELS = ['Basic', 'Conversational', 'Professional', 'Fluent'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESUME_RE = /\.(pdf|doc|docx)$/i;
const MULTI = new Set(['check', 'dynamic-check']);
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const emptyAnswers = () => Object.fromEntries(
  schema.flatMap((s) => s.fields).filter((f) => f.kind !== 'file').map((f) => [f.name, MULTI.has(f.kind) ? [] : '']),
);

const loadDraft = () => {
  try {
    const d = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (d && d.answers) return d;
  } catch (_) { /* ignore */ }
  return null;
};

const newSubmissionId = () => window.crypto?.randomUUID?.() || `sub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const isVisible = (field, answers) => {
  if (!field.condition) return true;
  const [name, values] = field.condition;
  const v = answers[name];
  return Array.isArray(v) ? v.some((x) => values.includes(x)) : values.includes(v);
};

export const spokenLanguages = (answers) => {
  const other = (answers.otherLanguage || '').trim();
  return (answers.languages || []).map((x) => (x === 'Other' ? other : x)).filter(Boolean);
};

const Choice = ({ type, name, value, checked, onChange, testId }) => (
  <label className="gf-choice">
    <input type={type} name={name} value={value} checked={checked} onChange={onChange} data-testid={testId} />
    <span>{value}</span>
    <i aria-hidden="true">✓</i>
  </label>
);

const slug = (v) => String(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const ApplicationFunnel = ({ onStepChange }) => {
  const draft = useMemo(loadDraft, []);
  const [answers, setAnswers] = useState(() => ({ ...emptyAnswers(), ...(draft?.answers || {}) }));
  const [levels, setLevels] = useState(() => draft?.levels || {});
  const [acks, setAcks] = useState({ accurate: false, contact: false });
  const [resume, setResume] = useState(null);
  const [step, setStep] = useState(() => draft?.step || 0);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [enterKey, setEnterKey] = useState(0);
  const submissionId = useRef(draft?.submissionId || newSubmissionId());
  const panelRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { onStepChange?.(result ? 6 : step); }, [step, result, onStepChange]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, levels, step, submissionId: submissionId.current }));
  }, [answers, levels, step]);

  const spoken = spokenLanguages(answers);

  const set = (name, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'languages' || name === 'otherLanguage') {
        const langs = spokenLanguages(next);
        if (!langs.includes(next.preferred)) next.preferred = '';
        next.reading = (next.reading || []).filter((x) => langs.includes(x) || x === 'Other' || x === 'I’d like to discuss');
      }
      return next;
    });
    setErrors((e) => { const n = { ...e }; delete n[name]; return n; });
    setMessage('');
  };

  const toggle = (name, value) => {
    const cur = answers[name] || [];
    set(name, cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]);
  };

  const validate = (i) => {
    const bad = {};
    if (i < 5) {
      schema[i].fields.forEach((f) => {
        if (!isVisible(f, answers)) return;
        if (f.kind === 'file') {
          if (resume && (resume.size > 5 * 1024 * 1024 || !RESUME_RE.test(resume.name))) bad[f.name] = true;
          return;
        }
        const v = answers[f.name];
        const empty = Array.isArray(v) ? v.length === 0 : !String(v || '').trim();
        if (f.required && empty) bad[f.name] = true;
        if (!empty && f.kind === 'email' && !EMAIL_RE.test(v.trim())) bad[f.name] = true;
        if (!empty && f.name === 'zip' && !/^[0-9]{5}$/.test(v.trim())) bad[f.name] = true;
        if (!empty && f.maxlength && String(v).length > f.maxlength) bad[f.name] = true;
      });
      if (i === 1) spoken.forEach((l) => { if (!LEVELS.includes(levels[l])) bad.languages = true; });
    } else if (!acks.accurate || !acks.contact) {
      setMessage('Please confirm both acknowledgments to submit your application.');
      return false;
    }
    setErrors(bad);
    if (Object.keys(bad).length) {
      setMessage('Please complete the highlighted fields. Résumé files must be PDF or Word and no larger than 5 MB.');
      const first = panelRef.current?.querySelector('.gf-field.invalid');
      first?.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
      first?.querySelector('input,select,textarea')?.focus({ preventScroll: true });
      return false;
    }
    setMessage('');
    return true;
  };

  const show = (i) => {
    setStep(i);
    setErrors({});
    setMessage('');
    setEnterKey((k) => k + 1);
    requestAnimationFrame(() => panelRef.current?.querySelector('.gf-step h3')?.focus({ preventScroll: true }));
  };

  const submit = async () => {
    if (!validate(5)) return;
    setSubmitting(true);
    setMessage('');
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify({
        answers: { ...answers, languageLevels: Object.fromEntries(spoken.map((l) => [l, levels[l]])) },
        acknowledgments: acks,
        client_submission_id: submissionId.current,
      }));
      if (resume) fd.append('resume', resume, resume.name);
      const { data } = await axios.post(`${API_URL}/api/hiring/apply`, fd);
      sessionStorage.removeItem(STORAGE_KEY);
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 422 && detail?.errors) {
        setMessage(`We couldn't accept the application yet: ${detail.errors.join(' ')} Use EDIT above to fix the answer and submit again.`);
      } else if (err.response && typeof detail === 'string') {
        setMessage(`${detail} Your answers are still here — adjust and submit again.`);
      } else {
        setMessage('We couldn’t reach our server. Your answers are saved on this page — check your connection and press SUBMIT APPLICATION again. You will not be entered twice.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (step < 5) { if (validate(step)) show(step + 1); return; }
    submit();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setResume(f);
    setErrors((er) => { const n = { ...er }; delete n.resume; return n; });
    if (f && (f.size > 5 * 1024 * 1024 || !RESUME_RE.test(f.name))) {
      setErrors((er) => ({ ...er, resume: true }));
      setMessage('Résumé files must be PDF or Word and no larger than 5 MB.');
    }
  };

  const renderInput = (f) => {
    const v = answers[f.name];
    const tid = `gf-${f.name}`;
    switch (f.kind) {
      case 'radio':
        return (
          <div className="gf-options" role="group" aria-label={f.label}>
            {f.options.map((o) => <Choice key={o} type="radio" name={f.name} value={o} checked={v === o} onChange={() => set(f.name, o)} testId={`${tid}-${slug(o)}`} />)}
          </div>
        );
      case 'check':
        return (
          <div className="gf-options" role="group" aria-label={f.label}>
            {f.options.map((o) => <Choice key={o} type="checkbox" name={f.name} value={o} checked={(v || []).includes(o)} onChange={() => toggle(f.name, o)} testId={`${tid}-${slug(o)}`} />)}
            {f.name === 'languages' && spoken.length > 0 && (
              <div id="gf-language-levels" style={{ gridColumn: '1 / -1' }}>
                {spoken.map((lang, i) => (
                  <div className="gf-language-select" key={lang}>
                    <label htmlFor={`language-${i}`}>Your comfort level in {lang}</label>
                    <select id={`language-${i}`} value={levels[lang] || ''} onChange={(e) => { setLevels((p) => ({ ...p, [lang]: e.target.value })); setErrors((er) => { const n = { ...er }; delete n.languages; return n; }); }} data-testid={`gf-level-${slug(lang)}`}>
                      <option value="">Select comfort level</option>
                      {LEVELS.map((l) => <option key={l}>{l}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'dynamic-select':
        return (
          <select id={tid} value={v} onChange={(e) => set(f.name, e.target.value)} data-testid={tid}>
            <option value="">Choose a language</option>
            {spoken.map((l) => <option key={l}>{l}</option>)}
          </select>
        );
      case 'dynamic-check': {
        const opts = [...new Set([...spoken, 'Other', 'I’d like to discuss'])];
        return (
          <div className="gf-options" role="group" aria-label={f.label}>
            {opts.map((o) => <Choice key={o} type="checkbox" name={f.name} value={o} checked={(v || []).includes(o)} onChange={() => toggle(f.name, o)} testId={`${tid}-${slug(o)}`} />)}
          </div>
        );
      }
      case 'select':
        return (
          <select id={tid} value={v} onChange={(e) => set(f.name, e.target.value)} data-testid={tid}>
            <option value="">Select an option</option>
            {f.options.map((o) => <option key={o}>{o}</option>)}
          </select>
        );
      case 'textarea':
        return <textarea id={tid} rows={3} maxLength={f.maxlength || undefined} value={v} onChange={(e) => set(f.name, e.target.value)} data-testid={tid} />;
      case 'file':
        return <input id={tid} type="file" accept=".pdf,.doc,.docx" ref={fileRef} onChange={onFile} data-testid={tid} />;
      case 'date':
        return <input id={tid} type="date" value={v} onChange={(e) => set(f.name, e.target.value)} data-testid={tid} />;
      default: {
        const extra = f.name === 'zip' ? { inputMode: 'numeric', maxLength: 5, autoComplete: 'postal-code' }
          : f.name === 'first' ? { autoComplete: 'given-name' } : f.name === 'last' ? { autoComplete: 'family-name' }
          : f.kind === 'email' ? { autoComplete: 'email' } : f.kind === 'tel' ? { autoComplete: 'tel' } : {};
        return <input id={tid} type={f.kind === 'text' ? 'text' : f.kind} value={v} onChange={(e) => set(f.name, e.target.value)} {...extra} data-testid={tid} />;
      }
    }
  };

  const renderField = (f) => {
    if (!isVisible(f, answers)) return null;
    const help = f.name === 'resume' ? 'Optional. PDF or Word file, up to 5 MB.' : f.help;
    return (
      <div className={`gf-field ${errors[f.name] ? 'invalid' : ''}`} key={f.name} data-field={f.name} data-testid={`gf-field-${f.name}`}>
        <label className="gf-label" htmlFor={`gf-${f.name}`}>{f.label} <small>{f.required ? 'REQUIRED' : 'OPTIONAL'}</small></label>
        {renderInput(f)}
        {f.name === 'resume' && resume && <p className="gf-help" data-testid="gf-resume-name">Selected: {resume.name}</p>}
        {help && <p className="gf-help">{help}</p>}
      </div>
    );
  };

  const display = (f) => {
    const v = answers[f.name];
    if (f.kind === 'file') return resume ? resume.name : 'Not provided';
    if (Array.isArray(v)) return v.length ? v.join(', ') : 'Not provided';
    return v || 'Not provided';
  };

  const renderReview = () => (
    <div id="gf-review" data-testid="gf-review">
      {schema.slice(0, 5).map((s, i) => (
        <div className="gf-review-group" key={s.title}>
          <div className="gf-review-head">
            <h4>{s.title}</h4>
            <button type="button" aria-label={`Edit ${s.title}`} onClick={() => show(i)} data-testid={`gf-edit-${i}`}>EDIT</button>
          </div>
          <dl>
            {s.fields.filter((f) => isVisible(f, answers)).map((f) => (
              <React.Fragment key={f.name}>
                <dt>{f.label}</dt><dd data-testid={`gf-review-${f.name}`}>{display(f)}</dd>
                {f.name === 'languages' && spoken.map((l) => (
                  <React.Fragment key={l}><dt>{l} — communication level</dt><dd>{levels[l] || 'Not provided'}</dd></React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );

  if (result) {
    return (
      <div className="gf-panel" data-testid="gf-complete-panel">
        <div id="gf-complete">
          <span className="gf-complete-icon">✓</span>
          <h3>Application received.</h3>
          <p data-testid="gf-complete-message">
            Thanks, {answers.first}. Your application has been saved and our team will review it. Selected candidates will be contacted about next steps — applying does not guarantee an interview or hire.
          </p>
          {result.receipt_email_status === 'sent' && <p data-testid="gf-receipt-note">A confirmation email was sent to {answers.email}.</p>}
          <div className="gf-next-stages">REVIEW → INTERVIEW → PAID ASSESSMENT → DECISION</div>
          <Link id="gf-return" to="/" style={{ display: 'inline-block', textDecoration: 'none' }} data-testid="gf-return-home">BACK TO THE HOMEPAGE</Link>
        </div>
      </div>
    );
  }

  const current = schema[step];
  return (
    <div className="gf-panel" ref={panelRef}>
      <div className="gf-progress-top">
        <span id="gf-count" data-testid="gf-count">{`0${step + 1} / 06`}</span>
        <span id="gf-stage" data-testid="gf-stage">{LABELS[step]}</span>
      </div>
      <div className="gf-track"><i style={{ width: `${((step + 1) / 6) * 100}%` }} /></div>
      <form id="guru-form" noValidate onSubmit={(e) => { e.preventDefault(); next(); }} data-testid="guru-application-form">
        <section className="gf-step gf-enter" key={enterKey} data-gf-step={step}>
          <h3 tabIndex={-1}>{current.title}</h3>
          <p className="gf-desc">{step === 5 ? 'Check your answers. You can edit any section before submitting.' : current.desc}</p>
          {step < 5 ? current.fields.map(renderField) : (
            <>
              {renderReview()}
              <label className="gf-consent">
                <input type="checkbox" name="accurate" checked={acks.accurate} onChange={(e) => setAcks((a) => ({ ...a, accurate: e.target.checked }))} data-testid="gf-ack-accurate" />
                <span>I confirm that the information I provided is accurate to the best of my knowledge.</span>
              </label>
              <label className="gf-consent">
                <input type="checkbox" name="contact" checked={acks.contact} onChange={(e) => setAcks((a) => ({ ...a, contact: e.target.checked }))} data-testid="gf-ack-contact" />
                <span>I understand that Gleam Guru may contact me about this application and that applying does not guarantee an interview, work or employment.</span>
              </label>
              <p className="gf-help">Your application is reviewed only by Gleam Guru hiring staff. <Link to="/privacy" style={{ color: '#ff8737' }}>Privacy Policy</Link></p>
            </>
          )}
        </section>
        <p id="gf-error" role="alert" data-testid="gf-error">{message}</p>
        <div className="gf-actions">
          <button type="button" id="gf-back" hidden={step === 0} onClick={() => show(Math.max(0, step - 1))} data-testid="gf-back">← BACK</button>
          <button type="submit" id="gf-next" disabled={submitting} data-testid={step === 5 ? 'gf-submit' : 'gf-next'}>
            {submitting ? 'SUBMITTING…' : step === 5 ? 'SUBMIT APPLICATION ↗' : 'CONTINUE →'}
          </button>
        </div>
      </form>
    </div>
  );
};

export const ApplicationSection = () => {
  const [step, setStep] = useState(0);
  return (
    <section className="guru-application" id="apply" aria-labelledby="application-title" data-testid="application-section">
      <div className="gf-intro">
        <p className="section-label">YOUR NEXT MOVE</p>
        <h2 id="application-title">Become <br />A <span>Guru.</span></h2>
        <p>Tell us about yourself and the work you’re looking for.</p>
        <ol className="gf-stage-list" data-testid="gf-stage-list">
          {STAGE_NAMES.map((n, i) => (
            <li key={n} className={i === step ? 'active' : i < step ? 'done' : ''} aria-current={i === step ? 'step' : undefined}>
              <span>{`0${i + 1}`}</span>{n}
            </li>
          ))}
        </ol>
      </div>
      <ApplicationFunnel onStepChange={setStep} />
    </section>
  );
};
